/**
 * formTokens.js
 *
 * Tokens específicos para formulários Premium.
 * Extende os tokens base do usePremiumTokens.
 */

import { useMemo } from 'react';
import { usePremiumTokens } from '../../../theme/usePremiumTokens';

/**
 * Gera tokens específicos para formulários
 * @param {Object} tokens - Tokens base do premium theme
 * @returns {Object} Tokens de formulário
 */
export const getFormTokens = (tokens) => ({
  // Input
  input: {
    background: tokens?.surface?.elevated,
    backgroundHover: tokens?.surface?.hover,
    backgroundFocus: tokens?.surface?.selected,
    backgroundDisabled: tokens?.surface?.muted,
    border: tokens?.border?.default,
    borderHover: tokens?.accent?.primary,
    borderFocus: tokens?.accent?.primary,
    borderError: tokens?.semantic?.error,
    borderSuccess: tokens?.semantic?.success,
    text: tokens?.text?.primary,
    textPlaceholder: tokens?.text?.muted,
    textDisabled: tokens?.text?.disabled,
    label: tokens?.text?.secondary,
    labelFocus: tokens?.accent?.primary,
    labelError: tokens?.semantic?.error,
    helper: tokens?.text?.muted,
    helperError: tokens?.semantic?.error,
    helperSuccess: tokens?.semantic?.success,
    iconColor: tokens?.text?.muted,
    iconColorFocus: tokens?.accent?.primary,
  },

  // Section
  section: {
    headerColor: tokens?.text?.muted,
    headerWeight: 600,
    headerSpacing: '0.05em',
    headerSize: '0.75rem',
    dividerColor: tokens?.border?.subtle,
    marginBottom: 3,
    gap: 2,
  },

  // Field Group
  fieldGroup: {
    gap: 2,
    gapMobile: 2,
  },

  // Validation
  validation: {
    errorBg: tokens?.semantic?.errorBg,
    errorBorder: tokens?.semantic?.error,
    errorText: tokens?.semantic?.error,
    successBg: tokens?.semantic?.successBg,
    successBorder: tokens?.semantic?.success,
    successText: tokens?.semantic?.success,
    warningBg: tokens?.semantic?.warningBg,
    warningBorder: tokens?.semantic?.warning,
    warningText: tokens?.semantic?.warning,
  },

  // Buttons
  button: {
    primary: {
      bg: tokens?.accent?.primary,
      bgHover: tokens?.accent?.hover,
      text: tokens?.text?.onAccent,
      shadow: tokens?.shadows?.sm,
      shadowHover: tokens?.shadows?.glowAccent,
    },
    secondary: {
      bg: 'transparent',
      bgHover: tokens?.accent?.subtle,
      border: tokens?.border?.default,
      borderHover: tokens?.accent?.primary,
      text: tokens?.text?.primary,
      textHover: tokens?.accent?.primary,
    },
    danger: {
      bg: tokens?.semantic?.error,
      bgHover: tokens?.semantic?.errorHover,
      text: '#ffffff',
    },
    ghost: {
      bg: 'transparent',
      bgHover: tokens?.surface?.hover,
      text: tokens?.text?.secondary,
      textHover: tokens?.text?.primary,
    },
  },

  // Container
  container: {
    background: tokens?.surface?.glass,
    border: tokens?.border?.subtle,
    borderRadius: tokens?.radius?.lg,
    backdropBlur: '12px',
    padding: 3,
  },

  // Accordion/Section
  accordion: {
    background: tokens?.surface?.glass,
    backgroundExpanded: tokens?.surface?.elevated,
    border: tokens?.border?.subtle,
    borderRadius: tokens?.radius?.lg,
    headerText: tokens?.text?.primary,
    headerWeight: 600,
    iconColor: tokens?.accent?.primary,
  },

  // Select/Dropdown
  select: {
    menuBackground: tokens?.surface?.elevated,
    menuBorder: tokens?.border?.subtle,
    menuShadow: tokens?.shadows?.lg,
    optionHover: tokens?.surface?.hover,
    optionSelected: tokens?.accent?.subtle,
    optionSelectedText: tokens?.accent?.primary,
  },

  // Checkbox/Switch
  checkbox: {
    uncheckedBg: tokens?.surface?.elevated,
    uncheckedBorder: tokens?.border?.default,
    checkedBg: tokens?.accent?.primary,
    checkedIcon: tokens?.text?.onAccent,
  },

  // File Input
  fileInput: {
    dropzoneBg: tokens?.surface?.elevated,
    dropzoneBorder: tokens?.border?.default,
    dropzoneBorderActive: tokens?.accent?.primary,
    dropzoneText: tokens?.text?.secondary,
    dropzoneIcon: tokens?.text?.muted,
  },

  // Transitions
  transitions: {
    fast: tokens?.transitions?.fast || '0.1s ease',
    normal: tokens?.transitions?.colors || '0.15s ease',
    slow: tokens?.transitions?.all || '0.2s ease',
  },
});

/**
 * Hook para usar form tokens
 * @returns {Object} Tokens de formulário
 */
export const useFormTokens = () => {
  const tokens = usePremiumTokens();
  return useMemo(() => getFormTokens(tokens), [tokens]);
};

/**
 * Gera estilos de input baseado no estado
 * @param {Object} formTokens - Tokens de formulário
 * @param {Object} state - Estado do input { error, success, disabled, focused }
 * @returns {Object} Estilos sx para MUI
 */
export const getInputStyles = (formTokens, state = {}) => {
  const { error, success, disabled, focused } = state;

  let borderColor = formTokens?.input?.border;
  let labelColor = formTokens?.input?.label;

  if (error) {
    borderColor = formTokens?.input?.borderError;
    labelColor = formTokens?.input?.labelError;
  } else if (success) {
    borderColor = formTokens?.input?.borderSuccess;
  } else if (focused) {
    borderColor = formTokens?.input?.borderFocus;
    labelColor = formTokens?.input?.labelFocus;
  }

  return {
    '& .MuiOutlinedInput-root': {
      backgroundColor: disabled
        ? formTokens?.input?.backgroundDisabled
        : formTokens?.input?.background,
      transition: formTokens?.transitions?.normal,
      '& fieldset': {
        borderColor,
        transition: formTokens?.transitions?.normal,
      },
      '&:hover fieldset': {
        borderColor: disabled ? borderColor : formTokens?.input?.borderHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: formTokens?.input?.borderFocus,
      },
    },
    '& .MuiInputLabel-root': {
      color: labelColor,
      '&.Mui-focused': {
        color: formTokens?.input?.labelFocus,
      },
    },
    '& .MuiInputBase-input': {
      color: disabled
        ? formTokens?.input?.textDisabled
        : formTokens?.input?.text,
      '&::placeholder': {
        color: formTokens?.input?.textPlaceholder,
        opacity: 1,
      },
    },
    '& .MuiFormHelperText-root': {
      color: error
        ? formTokens?.input?.helperError
        : success
          ? formTokens?.input?.helperSuccess
          : formTokens?.input?.helper,
    },
  };
};

/**
 * Gera estilos de botão baseado na variante
 * @param {Object} formTokens - Tokens de formulário
 * @param {'primary' | 'secondary' | 'danger' | 'ghost'} variant - Variante do botão
 * @returns {Object} Estilos sx para MUI
 */
export const getButtonStyles = (formTokens, variant = 'primary') => {
  const buttonTokens = formTokens?.button?.[variant] || formTokens?.button?.primary;

  return {
    backgroundColor: buttonTokens?.bg,
    color: buttonTokens?.text,
    borderColor: buttonTokens?.border,
    boxShadow: buttonTokens?.shadow,
    transition: formTokens?.transitions?.normal,
    '&:hover': {
      backgroundColor: buttonTokens?.bgHover,
      color: buttonTokens?.textHover || buttonTokens?.text,
      borderColor: buttonTokens?.borderHover || buttonTokens?.border,
      boxShadow: buttonTokens?.shadowHover || buttonTokens?.shadow,
    },
    '&:disabled': {
      opacity: 0.5,
    },
  };
};

export default getFormTokens;
